import React, { useRef, useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';

export default function AdvancedPullToRefresh({ onRefresh, onLoadingStateChange, children }) {
    const containerRef = useRef(null);
    const startY = useRef(0);

    const [pullDistance, setPullDistance] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const THRESHOLD = 90;
    const MIN_REFRESH_TIME = 400; // ms

    const handleTouchStart = (e) => {
        if (window.scrollY === 0) {
            startY.current = e.touches[0].clientY;
        } else {
            startY.current = 0;
        }
    };

    const handleTouchEnd = async () => {
        if (pullDistance > THRESHOLD) {
            setRefreshing(true);
            onLoadingStateChange?.(true); // Notify parent about loading state
            const start = Date.now();
            await onRefresh?.();
            const elapsed = Date.now() - start;
            if (elapsed < MIN_REFRESH_TIME) {
                setTimeout(() => {
                    setRefreshing(false);
                    onLoadingStateChange?.(false); // Notify parent loading is done
                }, MIN_REFRESH_TIME - elapsed);
            } else {
                setRefreshing(false);
                onLoadingStateChange?.(false); // Notify parent loading is done
            }
        }
        setPullDistance(0);
    };

    // System-cancelled gesture (incoming call, notification swipe, a bottom
    // sheet opening mid-pull, etc.) — reset without triggering a refresh.
    const handleTouchCancel = () => {
        startY.current = 0;
        setPullDistance(0);
    };

    // -------------------------------
    // FIX: attach passive=false listener
    // -------------------------------
    useEffect(() => {
        const el = containerRef.current;

        const handleMove = (e) => {
            if (startY.current && !refreshing) {
                const distance = e.touches[0].clientY - startY.current;
                if (distance > 0) {
                    e.preventDefault(); // now allowed
                    setPullDistance(Math.min(distance / 2, 150));
                }
            }
        };

        el.addEventListener('touchmove', handleMove, { passive: false });

        return () => {
            el.removeEventListener('touchmove', handleMove);
        };
    }, [refreshing]);
    // -------------------------------

    return (
        <div
            ref={containerRef}
            style={{ overscrollBehavior: 'contain', minHeight: '100vh' }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Pull indicator */}
            <div
                style={{
                    height: pullDistance,
                    transition: refreshing ? 'height 0.2s' : 'height 0.15s ease-out',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: pullDistance > 5 ? 1 : 0,
                }}
            >
                {refreshing ? (
                    <Spinner animation="border" size="sm" />
                ) : (
                    <div style={{ fontSize: 12, color: '#777' }}>
                        {pullDistance > THRESHOLD ? 'Release to refresh' : 'Pull to refresh'}
                    </div>
                )}
            </div>

            {children}
        </div>
    );
}
